import {
  ArrowLeft,
  Bell,
  Bookmark,
  LifeBuoy,
  type LucideIcon,
  MoreVertical,
  Plus,
  Search,
  Share,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { PencilIcon, SettingIcon, UploadIcon } from "@/components/icons"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useUnreadCount } from "@/features/notifications/hooks/useNotifications"
import { getAssetUrl } from "@/utils/assets"

type TopNavVariant = "main" | "back-more" | "my-rituality"

export interface DropdownAction {
  label: string
  icon?: LucideIcon
  onClick: () => void
}

export interface InlineAction {
  icon: "share" | "save"
  onClick: () => void
  active?: boolean
}

export interface MobileTopNavProps {
  variant: TopNavVariant
  onBack?: () => void
  onMore?: () => void
  avatarUrl?: string
  onSearch?: () => void
  onNotifications?: () => void
  onCreate?: () => void
  onShare?: () => void
  moreActions?: DropdownAction[]
  inlineActions?: InlineAction[]
}

export function MobileTopNav({
  variant,
  onBack,
  onMore,
  avatarUrl,
  onSearch,
  onNotifications,
  onCreate,
  onShare,
  moreActions,
  inlineActions,
}: MobileTopNavProps) {
  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      <div className="flex items-center justify-between px-5 h-[48px] pt-[env(safe-area-inset-top)] bg-transparent">
        {variant === "main" ? (
          <MainNavContent
            avatarUrl={avatarUrl}
            onSearch={onSearch}
            onNotifications={onNotifications}
            onCreate={onCreate}
          />
        ) : variant === "my-rituality" ? (
          <MyRitualityNavContent onShare={onShare} />
        ) : (
          <BackMoreNavContent
            onBack={onBack}
            onMore={onMore}
            moreActions={moreActions}
            inlineActions={inlineActions}
          />
        )}
      </div>
    </header>
  )
}

function MainNavContent({
  avatarUrl,
  onSearch,
  onNotifications,
  onCreate,
}: {
  avatarUrl?: string
  onSearch?: () => void
  onNotifications?: () => void
  onCreate?: () => void
}) {
  const navigate = useNavigate()
  const logoWordmark = getAssetUrl("auth-logo.png")
  const hamburger = getAssetUrl("hambuger_icon.svg")
  const { data: unreadCount } = useUnreadCount()

  return (
    <>
      <div className="flex justify-center items-center gap-5">
        <img src={hamburger} className="flex items-center justify-center" />
        <img
          src={logoWordmark}
          alt="Rituality"
          className="h-[21px] pb-1.25 cursor-pointer"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onCreate}
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
        >
          <Plus size={22} />
        </button>
        <button
          onClick={onSearch}
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
        >
          <Search size={22} />
        </button>
        <button
          onClick={onNotifications}
          className="relative flex items-center justify-center w-[36px] h-[36px] text-primary"
        >
          <Bell size={22} />
          {(unreadCount || 0) > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red text-white text-[10px] font-semibold rounded-full flex items-center justify-center px-1">
              {(unreadCount || 0) > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => navigate("/profile")}
          className="w-[32px] h-[32px] rounded-full overflow-hidden border border-white/20 ml-1"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-brand-olive/40" />
          )}
        </button>
      </div>
    </>
  )
}

function BackMoreNavContent({
  onBack,
  onMore,
  moreActions,
  inlineActions,
}: {
  onBack?: () => void
  onMore?: () => void
  moreActions?: DropdownAction[]
  inlineActions?: InlineAction[]
}) {
  const navigate = useNavigate()

  return (
    <>
      <button
        onClick={onBack || (() => navigate(-1))}
        className="flex items-center justify-center w-[44px] h-[44px] -ml-2 text-primary"
      >
        <ArrowLeft size={24} />
      </button>
      <div className="flex items-center gap-1">
        {inlineActions?.map((action, i) =>
          action.icon === "share" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label="Share"
            >
              <Share size={22} />
            </button>
          ) : action.icon === "save" ? (
            <button
              key={i}
              onClick={action.onClick}
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label="Save"
            >
              <Bookmark size={22} className={action.active ? "fill-current" : ""} />
            </button>
          ) : null,
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={onMore}
              className="flex items-center justify-center w-[44px] h-[44px] -mr-2 text-primary"
            >
              <MoreVertical size={24} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {moreActions?.map((action, i) => (
              <DropdownMenuItem key={i} onClick={action.onClick}>
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </DropdownMenuItem>
            ))}
            {moreActions && moreActions.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => window.open("mailto:support@myrituality.com", "_self")}
            >
              <LifeBuoy className="w-4 h-4" />
              Help and Support
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}

function MyRitualityNavContent({ onShare }: { onShare?: () => void }) {
  const navigate = useNavigate()

  const handleShare = () => {
    if (onShare) {
      onShare()
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success("Link copied to clipboard")
    }
  }

  return (
    <>
      <button
        type="button"
        className="flex items-center justify-center w-[44px] h-[44px] -ml-2 text-primary"
        onClick={() => navigate("/profile/edit")}
        aria-label="Edit Profile"
      >
        <PencilIcon className="!w-6 !h-6" />
      </button>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
          onClick={() => navigate("/settings")}
          aria-label="Settings"
        >
          <SettingIcon className="!w-6 !h-6" />
        </button>
        <button
          type="button"
          className="flex items-center justify-center w-[36px] h-[36px] text-primary"
          onClick={handleShare}
          aria-label="Share"
        >
          <UploadIcon className="!w-6 !h-6" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-center w-[36px] h-[36px] text-primary"
              aria-label="More"
            >
              <MoreVertical className="w-6 h-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => window.open("mailto:support@myrituality.com", "_self")}
            >
              <LifeBuoy className="w-4 h-4" />
              Help and Support
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}
